import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Package, MapPin, Calendar, TrendingUp, GitCompare, Info } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import SustainabilityBadge from '../components/common/SustainabilityBadge';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Batches = ({ user }) => {
  const [batches, setBatches] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/batches`);
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const toggleBatchSelection = (batchId) => {
    setSelectedBatches(prev => {
      if (prev.includes(batchId)) {
        return prev.filter(id => id !== batchId);
      }
      if (prev.length >= 3) {
        toast.error('You can compare up to 3 batches at a time');
        return prev;
      }
      return [...prev, batchId];
    });
  };

  const handleCompare = () => {
    if (selectedBatches.length < 2) {
      toast.error('Please select at least 2 batches to compare');
      return;
    }
    navigate(`/compare?ids=${selectedBatches.join(',')}`);
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'Delivered': 'bg-emerald-50 text-emerald-800 border-emerald-300',
      'In Transit': 'bg-slate-100 text-slate-700 border-slate-300',
      'Processing': 'bg-amber-50 text-amber-700 border-amber-300',
      'In Storage': 'bg-slate-100 text-slate-700 border-slate-300',
    };
    return statusMap[status] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NO', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading batches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="batches-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight text-slate-800 mb-2" style={{ fontFamily: 'Manrope' }}>
            Product Batches
          </h1>
          <p className="text-sm sm:text-lg text-slate-600">
            Track all seafood batches from Norwegian waters
          </p>
        </div>
        <div className="flex items-center justify-between gap-4 sm:justify-end">
          <div className="text-left sm:text-right">
            <p className="text-2xl sm:text-3xl font-bold text-slate-800">{batches.length}</p>
            <p className="text-xs sm:text-sm text-slate-600">Total Batches</p>
          </div>
          {selectedBatches.length >= 2 && (
            <Button onClick={handleCompare} data-testid="compare-button">
              <GitCompare className="w-4 h-4 mr-2" />
              Compare ({selectedBatches.length})
            </Button>
          )}
        </div>
      </div>

      {/* Batches Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6" data-testid="batches-grid">
        {batches.map((batch) => (
          <div
            key={batch.id}
            data-testid={`batch-card-${batch.id}`}
            className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 hover:shadow-lg hover:border-blue-300 transition-all duration-200 group relative"
          >
            <div className="absolute top-4 left-4 z-10">
              <Checkbox
                checked={selectedBatches.includes(batch.id)}
                onCheckedChange={() => toggleBatchSelection(batch.id)}
                onClick={(e) => e.stopPropagation()}
                data-testid={`batch-checkbox-${batch.id}`}
              />
            </div>
            
            <div onClick={() => navigate(`/batches/${batch.id}`)} className="cursor-pointer">
            {/* Header */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-4 ml-8">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Package className="w-5 h-5 text-slate-600 shrink-0" />
                  <span className="text-sm font-mono text-slate-600 truncate">{batch.batch_number}</span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-800 leading-tight group-hover:text-slate-900 transition-colors break-words">
                  {batch.product_name}
                </h3>
                <p className="text-sm text-slate-500 italic">{batch.species}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {batch.blockchain_verified && (
                  <div className="px-3 py-1 bg-slate-100 border border-slate-300 rounded-full text-xs font-medium text-slate-700">
                    ⛓️ Verified
                  </div>
                )}
              </div>
            </div>

            {/* Status Badge */}
            <div className="mb-4">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(batch.current_status)}`}>
                {batch.current_status}
              </span>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <MapPin className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wider">Origin</span>
                </div>
                <p className="text-sm font-medium text-slate-900">{batch.origin.name}</p>
                <p className="text-xs text-slate-500">{batch.origin.region}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wider">Catch Date</span>
                </div>
                <p className="text-sm font-medium text-slate-900">{formatDate(batch.catch_date)}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Package className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wider">Weight</span>
                </div>
                <p className="text-sm font-medium text-slate-900">{batch.weight_kg.toLocaleString()} kg</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wider">CO₂ Total</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs cursor-help">
                          Est
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Estimated (PoC)</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-sm font-medium text-slate-900">{batch.total_co2_kg} kg</p>
              </div>
            </div>

            {/* Compliance Badges */}
            <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-100">
              {batch.compliance.slice(0, 3).map((comp, idx) => (
                <SustainabilityBadge
                  key={idx}
                  standard={comp.standard}
                  status={comp.status}
                  description={comp.description}
                />
              ))}
            </div>

            {/* Integrity Score */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-600 uppercase tracking-wider">Integrity Score</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-slate-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-xs">
                          Integrity Score reflects data completeness, event verification, and traceability continuity within the PureSustain platform. The score may be affected by missing data, delayed events, or manual overrides. This score is not a certification or regulatory guarantee.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <span className="text-lg font-bold text-slate-900">{batch.integrity_score}/100</span>
              </div>
              <p className="text-xs text-slate-500 mb-2">Based on data completeness and verification</p>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-600 rounded-full"
                  style={{ width: `${batch.integrity_score}%` }}
                ></div>
              </div>
            </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Batches;
